import React, { useState } from "react";
import { Link } from "react-router-dom";
import { subscribe } from "../api";
import { usePrimaryProduct } from "../context/ProductContext";

// lucide-react intentionally excludes brand/trademarked logos, so the
// Instagram glyph is a small hand-rolled SVG rather than a library import.
const InstagramIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden="true"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const Footer: React.FC = () => {
  const { primaryProductId } = usePrimaryProduct();
  const shopLink = primaryProductId ? `/products/${primaryProductId}` : "/";
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      await subscribe(email, "footer");
      setSubmitted(true);
      setEmail("");
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status;
      if (status === 409) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-ks-surface border-t border-ks-border">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <img
              src="/logo.png"
              alt="KitStudio"
              className="h-10 w-auto mb-4 opacity-80"
            />
            <p className="text-ks-secondary font-montserrat text-sm leading-relaxed max-w-xs">
              Crafted for those who move with purpose. Every piece engineered
              for performance and presence.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-saira uppercase tracking-widest text-xs text-ks-muted mb-4">
              Navigate
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="font-montserrat text-sm text-ks-secondary hover:text-ks-text transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to={shopLink}
                  className="font-montserrat text-sm text-ks-secondary hover:text-ks-text transition-colors"
                >
                  Shop
                </Link>
              </li>
            </ul>
          </div>

          {/* Inline subscribe */}
          <div>
            <h4 className="font-saira uppercase tracking-widest text-xs text-ks-muted mb-4">
              Stay ahead of the drop
            </h4>
            <p className="font-montserrat text-sm text-ks-secondary mb-5">
              Early access to new releases and limited collections.
            </p>

            {submitted ? (
              <p className="font-saira uppercase tracking-widest text-xs text-ks-success">
                You're on the list.
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="w-full bg-ks-card border border-ks-border text-ks-text placeholder-ks-muted font-montserrat text-sm px-4 py-3 focus:outline-none focus:border-ks-secondary transition-colors"
                />
                {error && (
                  <p className="font-montserrat text-xs text-ks-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full border border-ks-border text-ks-text font-saira uppercase tracking-widest text-sm py-3 hover:bg-ks-card transition-colors disabled:opacity-50"
                >
                  {isLoading ? "Subscribing..." : "Subscribe"}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-ks-border mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-montserrat text-xs text-ks-muted">
            &copy; {new Date().getFullYear()} Kit Studio. All rights reserved.
          </p>
          <a
            href="https://www.instagram.com/kit_studio_eg/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow us on Instagram"
            className="text-ks-secondary hover:text-ks-text transition-colors"
          >
            <InstagramIcon />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
